import JsonResponse from "csi/comm/JsonResponse";
import PostRequest from "csi/comm/PostRequest";
import { ControllerSaveFunction } from "csi/middleware/common";
import { C8Node } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, makeObservable, observable } from "mobx";
import { createContext, useContext } from "react";

export default class CopyFoodStore extends ReactionStore {
	/** static values from settings, copied food, and locale */
	readonly multiCopyLimit: number;
	readonly enableCreateProductinProject: string;
	readonly food: string;
	readonly copyNumberErrorMessage: string;

	/** list of objects with urls and names */
	readonly projectItems: ComponentItem[];

    /** the parameters of the java operation */
    @observable private _leafFolder: string = "";
    @observable private _formulatedProductType: string;
    @observable private _foodName: string = "";
    @observable private _copyNumber: number = 1;
    @observable private _copySKUs: boolean = false;
    @observable private _project: string = "";

    /** states that are required for display functionality */
    @observable private _loading: boolean = false;
    @observable private _leafFolderError: boolean = false;

    /** function to close and return from the form */
    private onSave: ControllerSaveFunction;
    private onIncrementalSave: VoidFunction;

    @action setCopyNumber = (newValue: number): void => {
    	this._copyNumber = newValue ? newValue : 1;
    };

    @action setCopySKUs = (newValue: boolean): void => {
    	this._copySKUs = newValue;
    };

    @action setFoodName = (newValue: string): void => {
    	this._foodName = newValue;
    };

    @action setFormulatedProductType = (newValue: string): void => {
    	this._formulatedProductType = newValue;
    };

    @action setLeafFolderError = (newValue: boolean): void => {
    	this._leafFolderError = newValue;
    };

    @action setLoading = (newValue: boolean): void => {
    	this._loading = newValue;
    };

    @action setProject = (newValue: string): void => {
    	this._project = newValue;
    };

    @action setLeafFolder = (newValue: string): void => {
    	this._leafFolder = newValue;
    };

    /** the final folder of the product hierarchy structure */
    get leafFolder(): string {
    	return this._leafFolder;
    }

    /** the boolean of if the form has been submitted while the leaf folder null */
    get leafFolderError(): boolean {
    	return this._leafFolderError;
    }

    /** the amount of copies to make of the food */
    get copyNumber(): number {
    	return this._copyNumber;
    }

    /** whether or not to copy the SKUs of the food */
    get copySKUs(): boolean {
    	return this._copySKUs;
    }

    /** the base name of the new food, initialized from the copied food */
    get foodName(): string {
    	return this._foodName;
    }

    /** the subtype of the food */
    get formulatedProductType(): string {
    	return this._formulatedProductType;
    }

    /** the project associated with the food to copy */
    get project(): string {
    	return this._project;
    }

    /** variable to determine of the current form is loading to disable/enable UI elements */
    get loading(): boolean {
    	return this._loading;
    }

    /** the error message that displays when copyNumber exceeds multiCopyLimit */
    get copyNumberHelperText(): String {
    	return this.copyNumberError ? this.copyNumberErrorMessage : "";
    }

    /**
     * checks if the user input number of copies exceeds the setting specification
     * @returns boolean if user input copy number is greater than allowed setting
     */
    get copyNumberError(): boolean {
    	return this._copyNumber > this.multiCopyLimit;
    }

    /** builds the operation request and returns a promise */
    private buildRequest = (): Promise<JsonResponse> => {
    	const request = new PostRequest("FormulatedProduct", "CopyFood");

    	request.addParameter("Node Name", this.foodName);
    	request.addParameter("NumToCreate", this.copyNumber);
    	request.addParameter("Food", this.food);
    	request.addParameter("CopySKU", this.copySKUs);
    	request.addParameter("Project", this.project);
    	request.addParameter("ProductHierarchyFolder", this.leafFolder);

    	return request.post();
    };

    /** calls the copy food java operation and closes the popup */
    @action executeCopyFoodOperation = async (): Promise<void> => {
    	this.setLoading(true);
    	if (!this.copyNumberError && this.leafFolder) {
    		await this.onSave(this.buildRequest());
    	} else if (!this.leafFolder) {
    		this.setLeafFolderError(true);
    	}
    	this.setLoading(false);
    };

    /** calls the copy food java operation and keeps the popup open */
    @action executeIncrementalCopyFoodOperation = async (): Promise<void> => {
    	this.setLoading(true);
    	if (!this.copyNumberError && this.leafFolder) {
    		await this.buildRequest();
    		this.onIncrementalSave();
    	} else if (!this.leafFolder) {
    		this.setLeafFolderError(true);
    	}
    	this.setLoading(false);
    };


    /** creates a list of items, with the id being the url and the name being the name */
    initializeProjectValues(projects: C8Node[]): ComponentItem[] {
    	return [{ id: "", name: "" }].concat(projects.map((node) => {
    		return {
    			id: node.$URL,
    			name: node.$Name,
    		};
    	}));
    }

    /** @constructor */
    constructor(locale: ILocaleProvider, node: C8Node, cache, onSave: ControllerSaveFunction, onIncrementalSave: VoidFunction, projects: C8Node[]) {
    	super();
    	makeObservable(this);

    	// initialize from node and cache params
    	const foodSubtypeSettings = cache.getPathNode(node, "Child:Subtype/Child:Settings");
    	const foodSubtype = cache.getPathNode(node, "Child:Subtype");
    	const hierarchySubtypeSettings =
            cache.getPathNode(node, "Child:ProductHierarchyDetails/Child:ProductHierarchy/Child:Subtype/Child:Settings");

    	// set initial parameter values from cache and constructor
    	this.enableCreateProductinProject = hierarchySubtypeSettings.EnableCreateProductInProject;
    	this.multiCopyLimit = foodSubtypeSettings.MultiCopyLimit;
    	this.food = node.$URL;
    	this.copyNumberErrorMessage = locale.resource("FoodCopyError_TooMany");
    	this._formulatedProductType = foodSubtype.$Name;
    	this._foodName = node.$Name;

    	// set functions to call for operation execution
    	this.onSave = onSave;
    	this.onIncrementalSave = onIncrementalSave;

    	// process project values
    	this.projectItems = this.initializeProjectValues(projects);
    }
}

export const CopyFoodStoreContext = createContext<CopyFoodStore>(undefined);
export const useCopyFoodStore = (): CopyFoodStore => useContext(CopyFoodStoreContext);